import { Box, Button, Stack } from '@mui/material'
import { TProductSimple } from '../../../core/types/Product'
import { SaleDateFilter } from './filters/SaleDateFilter'
import { SearchTypeFilter } from './filters/SearchTypeFilter'
import { ProductFilter } from './filters/ProductFilter'
import { AffiliatedFilter } from './filters/AffiliatedFilter'
import { TAffiliatedSimple } from '../../../core/types/Affiliated'
import { PaymentMethodsFilter } from './filters/PaymentMethodsFilter'
import { StatusFilter } from './filters/StatusFilter'
import { useState } from 'react'
import SwipeableDrawerRightDialog from '../../../components/Dialogs/SwipeableDrawerRightDialog'
import {
  ICheckoutOrdersFilter,
  ITrackingFilter,
} from '../../../core/types/CheckoutOrder'
import TrackerFilter from './filters/TrackerFilter'

export interface IAdvancedFiltersProps {
  open: boolean
  products: TProductSimple[]
  affiliateds: TAffiliatedSimple[]
  startDateDefault: Date | null
  endDateDefault: Date | null
  onlyRangeDate: boolean
  filters: ICheckoutOrdersFilter
  hasTypeFilter?: boolean
  hasStatusFilter?: boolean
  hasTrackerFilter?: boolean
  setOpen: (value: boolean) => void
  handleCloseFilter: () => void
  onChangeFilters: (value: ICheckoutOrdersFilter) => void
}

export const AdvancedFilters = ({
  open,
  affiliateds,
  products,
  onlyRangeDate,
  filters,
  hasTypeFilter,
  hasStatusFilter = true,
  hasTrackerFilter = false,
  setOpen,
  handleCloseFilter,
  onChangeFilters,
}: IAdvancedFiltersProps) => {
  const [startDate, setStartDate] = useState<Date | null | undefined>(
    filters.startDate
  )
  const [endDate, setEndDate] = useState<Date | null | undefined>(
    filters.endDate
  )
  const [searchType, setSearchType] = useState(filters.sellerType)

  const [productIds, setProductIds] = useState<string[]>(filters.products ?? [])
  const [productsList, setProductLists] = useState<string[]>([])

  const [affiliatedIds, setAffiliatedIds] = useState<string[]>(
    filters.affiliateds ?? []
  )
  const [selectdAffiliateds, setSelectdAffiliateds] = useState<string[]>([])

  const [paymentIds, setPaymentIds] = useState<string[]>(filters.payments ?? [])
  const [selectdPaymentsMethods, setSelectdPaymentsMethods] = useState<
    string[]
  >([])

  const [status, setStatus] = useState<string[]>(filters.status ?? [])
  const [selectdStatus, setSelectdStatus] = useState<string[]>([])

  const [track, setTrack] = useState<ITrackingFilter>({
    src: filters.src,
    sck: filters.sck,
    utm_campaign: filters.utm_campaign,
    utm_source: filters.utm_source,
    utm_medium: filters.utm_medium,
    utm_content: filters.utm_content,
    utm_term: filters.utm_term,
  })

  const clearTrack = () => {
    setTrack({
      src: null,
      sck: null,
      utm_campaign: null,
      utm_source: null,
      utm_medium: null,
      utm_content: null,
      utm_term: null,
    })
  }

  const handleCleanFilters = () => {
    setStartDate(new Date(1900, 0, 1))
    setEndDate(new Date())
    setSearchType(0)
    setProductIds([])
    setProductLists([])
    setAffiliatedIds([])
    setSelectdAffiliateds([])
    setPaymentIds([])
    setSelectdPaymentsMethods([])

    if (hasStatusFilter) {
      setStatus([])
      setSelectdStatus([])
    }

    clearTrack()
  }

  const handleDateFilter = (value: any) => {
    setStartDate(value.startDate)
    setEndDate(value.endDate)
  }

  const handleChangeFilter = () => {
    onChangeFilters({
      ...filters,
      ...track,
      startDate,
      endDate,
      affiliateds: affiliatedIds,
      payments: paymentIds,
      products: productIds,
      sellerType: searchType,
      status: hasStatusFilter ? status : filters.status,
    })
    handleCloseFilter()
  }

  const handleChangeCancel = () => {
    const oldProductLists = products
      .filter((p) => (filters.products ?? []).includes(p.id))
      .map((p) => p.name)

    const oldSelectedAffiliateds = affiliateds
      .filter((a) => (filters.affiliateds ?? []).includes(a.id))
      .map((a) => a.name)

    setStartDate(filters.startDate)
    setEndDate(filters.endDate)
    setSearchType(filters.sellerType)
    setProductIds(filters.products ?? [])
    setProductLists(oldProductLists)
    setAffiliatedIds(filters.affiliateds ?? [])
    setSelectdAffiliateds(oldSelectedAffiliateds)
    /*setPaymentIds([])
    setSelectdPaymentsMethods([])
    setStatus([])
    setSelectdStatus([])*/

    handleCloseFilter()

    setTrack({
      src: filters.src,
      sck: filters.sck,
      utm_campaign: filters.utm_campaign,
      utm_source: filters.utm_source,
      utm_medium: filters.utm_medium,
      utm_content: filters.utm_content,
      utm_term: filters.utm_term,
    })
  }

  return (
    <SwipeableDrawerRightDialog
      open={open}
      onClose={handleChangeCancel}
      setOpen={setOpen}
      title={
        <Stack direction="row" spacing={1} alignItems="center">
          <Box>Filtros avançados</Box>
          <Button
            variant="text"
            size="small"
            onClick={handleCleanFilters}
            sx={{ textTransform: 'none' }}
          >
            Limpar Filtros
          </Button>
        </Stack>
      }
      buttons={[
        { title: 'Cancelar', onClick: handleChangeCancel, type: 'negative' },
        { title: 'Filtrar', onClick: handleChangeFilter, type: 'positive' },
      ]}
    >
      <Stack direction="column" spacing={2} sx={{ width: '100%', pt: 1 }}>
        <SaleDateFilter
          startDate={startDate}
          endDate={endDate}
          handleDateFilter={handleDateFilter}
        />

        {!onlyRangeDate && (
          <>
            {hasTypeFilter && (
              <SearchTypeFilter
                typeFilterValue={searchType ?? 0}
                handleTypeFilter={setSearchType}
              />
            )}

            <ProductFilter
              products={products}
              handleProductFilter={setProductIds}
              productsList={productsList}
              setProductLists={setProductLists}
            />

            <AffiliatedFilter
              affiliateds={affiliateds}
              handleAffiliatedFilter={setAffiliatedIds}
              affiatedList={selectdAffiliateds}
              setAffiliatedList={setSelectdAffiliateds}
            />

            <PaymentMethodsFilter
              handlePaymentMethodFilter={setPaymentIds}
              selectds={selectdPaymentsMethods}
              setSelectds={setSelectdPaymentsMethods}
            />

            {hasStatusFilter && (
              <StatusFilter
                handleStatusFilter={setStatus}
                selectds={selectdStatus}
                setSelectds={setSelectdStatus}
              />
            )}
          </>
        )}

        {hasTrackerFilter && <TrackerFilter data={track} setData={setTrack} />}
      </Stack>
    </SwipeableDrawerRightDialog>
  )
}
